/* eslint-disable no-unused-vars */
import React, { useCallback } from 'react';

import { ApolloError } from '@apollo/client';

import ReCaptcha from '@/components/ReCaptcha';
import { logWarn } from '@/helpers/logger/logUtils';
import { isRecaptchaError } from '@/helpers/recaptchaUtils';

import useReCaptchaCallback from './useRecaptchaCallback';
import { useReCaptchaV2 } from './useRecaptchaV2';

const useRecaptchaValidation = (
  onSubmitWithToken: (value: string | any, token: string, version: 'v2' | 'v3') => Promise<void>,
  action: string = 'claimV3',
  actionV2: string = 'claimV2',
) => {
  const {
    captchaV2Token,
    showV2Captcha,
    hideV2Captcha,
    setCaptchaToken,
    isUseRecaptchaV2,
    isCaptchaV2Error,
    setRecaptchaV2Error,
  } = useReCaptchaV2();

  const handleV2RecaptchaComplete = useCallback(
    (token: string) => {
      setRecaptchaV2Error(false);
      setCaptchaToken(token);
    },
    [setCaptchaToken, setRecaptchaV2Error],
  );

  const validateWithV3Recaptcha = useReCaptchaCallback({
    action,
    callback: async (rcToken: string, value: string | any) => {
      try {
        await onSubmitWithToken(value, rcToken, 'v3');
      } catch (error) {
        logWarn(`RECAPTCHA validation error: ${error}`);
        if (isRecaptchaError(error as ApolloError)) {
          setCaptchaToken(null);
          setRecaptchaV2Error(false);
          showV2Captcha();
        }
      }
    },
    onV3Failure: () => {
      setCaptchaToken(null);
      setRecaptchaV2Error(false);
      showV2Captcha();
    },
  });

  const validateWithRecaptcha = useCallback(
    async (value: string | any) => {
      if (!isUseRecaptchaV2) {
        validateWithV3Recaptcha(value);
        return;
      }

      if (!captchaV2Token) {
        setRecaptchaV2Error(false);
        showV2Captcha();
        return;
      }

      try {
        await onSubmitWithToken(value, captchaV2Token, 'v2');
        hideV2Captcha();
        setRecaptchaV2Error(false);
      } catch (error) {
        setCaptchaToken(null);
        setRecaptchaV2Error(true);
        logWarn(`RECAPTCHA V2 validation error: ${error}`);
      }
    },
    [
      captchaV2Token,
      hideV2Captcha,
      isUseRecaptchaV2,
      onSubmitWithToken,
      setCaptchaToken,
      setRecaptchaV2Error,
      showV2Captcha,
      validateWithV3Recaptcha,
    ],
  );

  const RecaptchaV2Component = () => {
    if (!isUseRecaptchaV2 || captchaV2Token) {
      return null;
    }

    return <ReCaptcha action={actionV2} callback={handleV2RecaptchaComplete} isResetNeeded={false} />;
  };

  return {
    captchaV2Token,
    isUseRecaptchaV2,
    isLoading: false,
    isCaptchaV2Error,
    RecaptchaV2Component,
    handleV2RecaptchaComplete,
    setCaptchaToken,
    showV2Captcha,
    hideV2Captcha,
    validateWithRecaptcha,
  };
};

export default useRecaptchaValidation;
